import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map as mapObservable } from 'rxjs/operators';
import { AlertDefaults, AlertsApi, SendAlertRequest, SendAlertResponse } from 'src/app/hickory-api';
import { ApiConfig } from 'src/app/shared/api-config/api-config';

export interface ExtSendAlertRequest {
  files?: Array<File>,
  socialMediaFile?: File,
  settings?: AlertDefaults;
  templateId?: number;
}

interface Message {
  severity: string;
  message: string;
}

interface Status {
  isSuccess: boolean;
  errorCode: string;
  errorMessage: string;
  messages: Message[];
}

interface ErrorData {
  status: Status;
  hasErrors: boolean;
  isValid: boolean;
}

interface ErrorResponse {
  status: number;
  statusText: string;
  url: string;
  ok: boolean;
  name: string;
  message: string;
  error: ErrorData;
}

export class AlertApiHelper extends AlertsApi {
  static readonly validationErrorCodes = ['ValidationError', 'InvalidRequest'];

  static isValidationError(err: ErrorResponse): boolean {
    return this.validationErrorCodes.includes(err?.error?.status?.errorCode);
  }

  static getValidationErrorMessage(err: ErrorResponse): string {
    return this.isValidationError(err) ? err?.error?.status?.errorMessage : null;
  }

  static hasMultipleErrorMessages(err: ErrorResponse): boolean {
    return err?.error?.status?.messages?.length > 1;
  }

  static getValidationErrorMessages(err: ErrorResponse): string[] {
    return err?.error?.status?.messages?.map(item => item.message);
  }

  readonly PARAM_ALERT = 'alert';
  readonly PARAM_ASYNC = 'async';
  readonly PARAM_ATTACHMENT = 'attachment';
  readonly PARAM_SOCIAL_FILE = 'socMedImageFile'
  readonly API_PATH_SEND = '/v2/alertsWithAttachments';
  readonly API_PATH_SCHEDULE = '/v2/alerts/scheduled';

  constructor(httpClient: HttpClient) {
    super(httpClient, ApiConfig.hickoryConfigFactory().basePath, ApiConfig.hickoryConfigFactory());
  }

  sendAlert(request: ExtSendAlertRequest): Observable<SendAlertResponse> {
    // verify required parameter is not null or undefined
    if (!request?.settings) {
      throw new Error('Required parameter was null or undefined when calling sendAlert.');
    }

    const headers = this.defaultHeaders;
    const alert: SendAlertRequest = { settings: request.settings };
    const scheduled = !!alert.settings?.schedule?.scheduled;
    const path = this.configuration.basePath + (scheduled ? this.API_PATH_SCHEDULE : this.API_PATH_SEND);

    if (request.templateId) alert.templateId = request.templateId;
    if (!scheduled) alert.async = true; // ROAD-6648 -- don't include "async" for scheduled alerts

    // build the multipart form data...
    const formData = new FormData();
    // append attachments, if any
    request.files?.forEach(file => formData.append(this.PARAM_ATTACHMENT, file, file.name));
    // append social media attachment, if any
    if (request.socialMediaFile) formData.append(this.PARAM_SOCIAL_FILE, request.socialMediaFile, request.socialMediaFile.name);
    // append the alert settings
    formData.append(this.PARAM_ALERT, JSON.stringify(alert));
    // append async if not scheduled
    if (!scheduled) formData.append(this.PARAM_ASYNC, JSON.stringify(true));

    return this.httpClient.post<SendAlertResponse>(path, formData, {
      headers: headers,
      observe: 'response',
      responseType: 'json'
    }).pipe(mapObservable(response => {
      if (response.status === 204) {
        return undefined;
      } else {
        return response.body || {};
      }
    }));
  }
}
